import { useCustomRoute } from '../../hook/useCustomRoute';
import Main from './main/Main';
import ScreenId from './main/ScreenId';
import Title from './main/Title';
import IframePanel from './IframePanel';
import { useIframeStore } from '../../store/useIframe';
import { useLocation } from 'react-router-dom';
import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * @author taulmax
 * @date 2024.10.07
 * @description 메인 섹션 컴포넌트
 */
const RightSection = () => {
  const { routeConfig } = useCustomRoute();
  const { menu, sid, component } = routeConfig || {};
  const { isOpen, iframeWidth, setIframeWidth } = useIframeStore();
  const location = useLocation();
  
  // 리사이징 관련 state
  const [isResizing, setIsResizing] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeFrameRef = useRef<number>();
  const tutorialTimeoutRef = useRef<NodeJS.Timeout>();

  // 마우스 이동 핸들러
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!containerRef.current || !isResizing) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const newWidth = ((containerWidth - e.clientX + containerRect.left) / containerWidth) * 100;
    
    // 최소/최대 너비 제한 (20% ~ 80%)
    const clampedWidth = Math.max(20, Math.min(80, newWidth));
    setIframeWidth(clampedWidth);
  }, [isResizing]);

  // 마우스 업 핸들러
  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // 마우스 다운 핸들러
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  // 리사이징 이벤트 등록/해제
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // iframe이 열릴 때마다 튜토리얼 표시 (/bizmsg/sender 페이지에서만)
  useEffect(() => {
    if (isOpen && location.pathname === '/bizmsg/sender') {
      // 1초 후에 튜토리얼 표시
      tutorialTimeoutRef.current = setTimeout(() => {
        setShowTutorial(true);
        // 5초 후에 자동으로 사라짐
        setTimeout(() => {
          setShowTutorial(false);
        }, 5000);
      }, 1000);
    } else {
      setShowTutorial(false);
    }
    
    return () => {
      if (tutorialTimeoutRef.current) {
        clearTimeout(tutorialTimeoutRef.current);
      }
    };
  }, [isOpen, location.pathname]);

  return (
    <div className="!flex !flex-col !h-full" ref={containerRef}>
      {/* 헤더 - 고정 높이 */}
      <header className="!bg-white !shadow-sm !border-b !border-gray-200 !flex-shrink-0">
        <Title menu={menu} />
      </header>

      {/* 메인 영역 - iframe이 열렸을 때는 가로로 분할 */}
      <div className="!flex-grow !flex !min-h-0 !relative">
        {/* 메인 컨텐츠 - iframe이 열렸을 때는 너비 조정 */}
        <main 
          className={`!bg-[#F1F1F4] !overflow-auto ${location.pathname === '/bizmsg/sender' ? '' : 'md:!px-6 md:!pt-1 md:!pb-6'}`}
          style={{ 
            width: isOpen ? `calc(100% - ${iframeWidth}%)` : '100%',
            transition: isResizing ? 'none' : 'width 0.3s ease'
          }}
        >
          <Main component={component} />
        </main>
        
        {/* iframe 패널 - 오른쪽 */}
        {isOpen && (
          <>
            {/* 리사이징 핸들러 */}
            <div
              onMouseDown={handleMouseDown}
              style={{
                position: 'absolute',
                right: `${iframeWidth}%`,
                top: 0,
                bottom: 0,
                width: '12px',
                cursor: 'col-resize',
                backgroundColor: isResizing ? '#6B7280' : 'transparent',
                transition: 'background-color 0.2s',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#E5E7EB';
              }}
              onMouseLeave={(e) => {
                if (!isResizing) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div 
                style={{
                  width: '8px',
                  height: '240px',
                  background: 'linear-gradient(to bottom, #374151, #4B5563, #374151)',
                  borderRadius: '4px',
                  pointerEvents: 'none',
                  boxShadow: `
                    0 4px 12px rgba(0, 0, 0, 0.3),
                    inset 0 1px 0 rgba(255, 255, 255, 0.2),
                    inset 0 -1px 0 rgba(0, 0, 0, 0.2)
                  `,
                  position: 'relative',
                  border: '1px solid rgba(0, 0, 0, 0.2)'
                }}
              >
                {/* 가운데 점 3개 - 더 선명하게 */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#F3F4F6',
                    borderRadius: '50%',
                    boxShadow: `
                      0 1px 3px rgba(0, 0, 0, 0.4),
                      inset 0 1px 1px rgba(255, 255, 255, 0.3)
                    `,
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }} />
                  <div style={{
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#F3F4F6',
                    borderRadius: '50%',
                    boxShadow: `
                      0 1px 3px rgba(0, 0, 0, 0.4),
                      inset 0 1px 1px rgba(255, 255, 255, 0.3)
                    `,
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }} />
                  <div style={{
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#F3F4F6',
                    borderRadius: '50%',
                    boxShadow: `
                      0 1px 3px rgba(0, 0, 0, 0.4),
                      inset 0 1px 1px rgba(255, 255, 255, 0.3)
                    `,
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }} />
                </div>
              </div>
            </div>
            
            {/* iframe 컨테이너 */}
            <div 
              style={{ 
                width: `${iframeWidth}%`,
                minWidth: '300px',
                maxWidth: '80%',
                position: 'relative',
                transition: isResizing ? 'none' : 'width 0.3s ease'
              }}
            >
              <IframePanel />
            </div>
            
            {/* 리사이징 중 오버레이 (iframe 위 마우스 이동 시 끊김 방지) */}
            {isResizing && (
              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 20,
                  cursor: 'col-resize'
                }}
              />
            )}
            
            {/* 리사이징 튜토리얼 */}
            {showTutorial && (
              <div
                style={{
                  position: 'absolute',
                  right: `calc(${iframeWidth}% + 20px)`,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 30,
                  animation: 'tutorialPulse 2s ease-in-out infinite',
                  pointerEvents: 'none'
                }}
              >
                {/* 말풍선 */}
                <div
                  style={{
                    position: 'relative',
                    backgroundColor: '#4B5563',
                    color: 'white',
                    padding: '16px 24px',
                    borderRadius: '12px',
                    fontSize: '18px',
                    fontWeight: '700',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                    whiteSpace: 'nowrap',
                    lineHeight: '1.4'
                  }}
                >
                  <div style={{ marginBottom: '8px' }}>
                    ◀ 이 막대를 좌우로 드래그하면
                  </div>
                  <div>
                    화면 크기를 조절할 수 있어요! 👋
                  </div>
                  {/* 말풍선 꼬리 */}
                  <div
                    style={{
                      position: 'absolute',
                      right: '-10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 0,
                      height: 0,
                      borderTop: '10px solid transparent',
                      borderBottom: '10px solid transparent',
                      borderLeft: '10px solid #4B5563'
                    }}
                  />
                </div>
                
                {/* 화살표 애니메이션 */}
                <div
                  style={{
                    position: 'absolute',
                    right: '-70px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    animation: 'arrowMove 1.5s ease-in-out infinite'
                  }}
                >
                  <svg
                    width="50"
                    height="40"
                    viewBox="0 0 50 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 20 L35 20 M25 10 L35 20 L25 30"
                      stroke="#6B7280"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            )}
            
            {/* 애니메이션 스타일 */}
            {showTutorial && (
              <style dangerouslySetInnerHTML={{
                __html: `
                  @keyframes tutorialPulse {
                    0%, 100% { opacity: 0.9; transform: translateY(-50%) scale(1); }
                    50% { opacity: 1; transform: translateY(-50%) scale(1.05); }
                  }
                  
                  @keyframes arrowMove {
                    0%, 100% { transform: translateY(-50%) translateX(0); }
                    50% { transform: translateY(-50%) translateX(10px); }
                  }
                `
              }} />
            )}
          </>
        )}
      </div>
      
      {/* 푸터 - flex-shrink-0으로 항상 하단에 고정 */}
      <footer className="!flex-shrink-0">
        <ScreenId sid={sid} />
      </footer>
    </div>
  );
};

export default RightSection;
